import React from 'react';
import { Mail, Users, Lightbulb, Target, Heart, Globe } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const CareersPage: React.FC = () => {
  useSEO('careers' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  const values = [
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Innovation First",
      description: "We embrace cutting-edge technologies and creative solutions to solve complex problems."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Collaborative Culture",
      description: "We believe in the power of teamwork and diverse perspectives to achieve extraordinary results."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Excellence Driven",
      description: "We maintain the highest standards in everything we do, from code quality to client relationships."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "People Focused",
      description: "We prioritize work-life balance, personal growth, and creating meaningful impact."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Global Impact",
      description: "We work on projects that make a difference in industries ranging from healthcare to enterprise solutions."
    }
  ];


  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
            Join Our Team
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
            Build the future of AI-powered solutions with a team of passionate innovators
          </p>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            At Cognitive Craft Hub, we're revolutionizing how businesses leverage artificial intelligence. 
            Join us in creating cutting-edge solutions that make a real difference in the world.
          </p>
        </div>
      </section>

      {/* Company Values */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Our Values</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{value.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Current Openings */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Current Openings</h2>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-12 text-center">
            <div className="max-w-2xl mx-auto">
              <Users className="w-16 h-16 text-blue-600 dark:text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                No Current Openings
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                While we don't have any open positions at the moment, we're always interested in connecting 
                with talented individuals who share our passion for innovation and excellence.
              </p>
              <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 border border-blue-200 dark:border-blue-400/30">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Interested in Future Opportunities?
                </h4>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Send us your resume and a brief introduction. We'll keep your information on file 
                  and reach out when relevant positions become available.
                </p>
                <div className="flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
                  <Mail className="w-5 h-5" />
                  <a 
                    href="mailto:team@cognitivecrafthub.com?subject=Career Interest - Future Opportunities"
                    className="font-medium hover:underline"
                  >
                    team@cognitivecrafthub.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work With Us */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Make an Impact?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Whether you're a seasoned professional or just starting your career, we'd love to hear from you. 
              Join us in shaping the future of AI-powered solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:team@cognitivecrafthub.com?subject=Career Interest - General Inquiry"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </a>
              <a
                href="/contact"
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Learn More About Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Employment Law Disclaimer */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              <strong>Equal Opportunity Employer:</strong> Cognitive Craft Hub is committed to providing equal employment opportunities 
              to all qualified candidates without regard to race, color, religion, sex, sexual orientation, gender identity, 
              national origin, age, disability, or veteran status. We comply with all applicable federal, state, and local laws 
              governing non-discrimination in employment. Benefits described are subject to plan terms and eligibility requirements. 
              No employment offer is made through this website. All employment decisions are contingent upon successful completion 
              of background checks and other pre-employment requirements as permitted by law.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};